import React from "react";
import DocuementSearch from "../classes/DocumentFunctions";
import DocumentsTable from "../components/projectdocuments/DocumentsTable";
import {
    apiDelete,
    apiGet,
    apiPatch,
    useDataUnlessForbidden
} from "../services/api";
import { useSnackbar } from "notistack";
import IContainer from "./IContainer";
import IProjectHeader from "../models/IProjectHeader";
import IDocument from "../models/IDocument";
import IDocumentError from "../models/IDocumentError";
import ICategory from "../models/ICategory";

import {
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Button
} from "@material-ui/core";
import ProjectDocumentSource from "../models/ProjectDocumentSource";
import { SearchBox } from "../components/shared/SearchBox";
import { makeStyles } from "@material-ui/core";
import UploadDialog from "../components/projectdocuments/UploadDialog";
import EditDocumentDialog from "../components/projectdocuments/EditDocumentDialog";
import IDocumentEdited from "../models/IDocumentEdited";

interface IManageProjectDocumentsContainer extends IContainer {
    projectId: string;
}

const ManageProjectDocumentsContainer: React.FC<IManageProjectDocumentsContainer> = ({
    setSubtitle,
    projectId,
    setIsForbidden
}) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [radioSourceValue, setRadioSourceValue] = React.useState(
        ProjectDocumentSource.All
    );
    const [searchText, setSearchText] = React.useState("");
    const [showFilters, setShowFilters] = React.useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [
        documentToEdit,
        setDocumentToEdit
    ] = React.useState<IDocument | null>(null);

    const [filteredDocuments, setFilteredDocuments] = React.useState<
        IDocument[] | null
    >([]);

    const useStyles = makeStyles(theme => ({
        root: {
            minHeight: "500px"
        },
        search: {
            margin: theme.spacing(0, 0, 4, 0)
        },
        buttons: {
            padding: theme.spacing(4, 8, 4),
            [theme.breakpoints.down("xs")]: {
                padding: theme.spacing(2)
            }
        },
        formControl: {
            verticalAlign: "middle"
        },
        label: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "0.9em"
            }
        },
        radio: {
            [theme.breakpoints.down("xs")]: {
                padding: "5px"
            }
        }
    }));

    const [
        filteredSearchDocuments,
        setFilteredSearchDocuments
    ] = React.useState<IDocument[] | null>([]);

    const [
        project,
        isProjectLoading,
        isProjectForbidden
    ] = useDataUnlessForbidden<IProjectHeader>(
        `projects/${projectId}`,
        () =>
            enqueueSnackbar(
                "We couldn't retrieve the project information. Please log out and try again.",
                { variant: "error" }
            )
    );

    const [categories, isLoadingCategories] = useDataUnlessForbidden<
        ICategory[]
    >(`projects/all/${projectId}/types`, () =>
        enqueueSnackbar(
            "We couldn't retrieve the project categories. Please log out and try again.",
            { variant: "error" }
        )
    );

    const [userCategories] = useDataUnlessForbidden<ICategory[]>(
        `projects/all/${projectId}/types/me`,
        () =>
            enqueueSnackbar(
                "We couldn't retrieve the project categories. Please log out and try again.",
                { variant: "error" }
            )
    );

    const removeDocumentHandler = async (document: IDocument) => {
        var skey = enqueueSnackbar(
            `Deleting the selected document '${document.name}'.`,
            { variant: "info" }
        );

        let success = true;

        apiDelete(`projects/all/${projectId}/documents/${document.id}`)
            .catch(() => {
                success = false;

                if (skey) {
                    closeSnackbar(skey);
                }
            })
            .then(() => {
                if (skey) {
                    closeSnackbar(skey);
                }

                if (success) {
                    refreshProjectDocuments();

                    enqueueSnackbar(`Deleted document '${document.name}'.`, {
                        variant: "success"
                    });
                } else {
                    enqueueSnackbar(
                        `We couldn't delete the selected document '${document.name}'. Please try again later.`,
                        { variant: "error" }
                    );
                }
            });
    };

    const [
        documents,
        isLoadingDocuments,
        isDocumentsForbidden,
        refreshProjectDocuments
    ] = useDataUnlessForbidden<IDocument[]>(
        `projects/all/${projectId}/documents`,
        () =>
            enqueueSnackbar(
                "We couldn't retrieve the document information. Please log out and try again.",
                { variant: "error" }
            )
    );

    React.useEffect(() => {
        setIsForbidden(isProjectForbidden || isDocumentsForbidden);
    }, [isProjectForbidden, isDocumentsForbidden, setIsForbidden]);

    React.useEffect(() => {
        if (!isProjectLoading && project !== null && !isLoadingDocuments) {
            setSubtitle(
                `Documents associated with this project '${project!.name}'`
            );
            setFilteredDocuments(documents);
            setFilteredSearchDocuments(documents);
        }
    }, [project, documents]);

    React.useEffect(
        () => setShowFilters(documents !== null && documents.length > 0),
        [searchText, documents, filteredDocuments]
    );

    React.useEffect(() => {
        switch (radioSourceValue) {
            case ProjectDocumentSource.All:
                if (documents !== null) {
                    setFilteredDocuments(documents);
                    setFilteredSearchDocuments(documents);
                    setSearchText("");
                }
                break;
            case ProjectDocumentSource.Customer:
                if (documents !== null) {
                    let filtered = documents.filter(
                        element =>
                            element.source === ProjectDocumentSource.Customer
                    );
                    setFilteredDocuments(filtered);
                    setFilteredSearchDocuments(filtered);
                    setSearchText("");
                }
                break;
            case ProjectDocumentSource.Tracerco:
                if (documents !== null) {
                    let filtered = documents.filter(
                        element =>
                            element.source === ProjectDocumentSource.Tracerco
                    );
                    setFilteredDocuments(filtered);
                    setFilteredSearchDocuments(filtered);
                    setSearchText("");
                }
                break;
        }
    }, [radioSourceValue, documents]);

    React.useEffect(() => {
        const filtered = (filteredDocuments || []).filter(d =>
            DocuementSearch.ContainsSerachText(d, searchText)
        );
        setFilteredSearchDocuments(filtered);
    }, [searchText]);

    const handleSourceFilterChange = event => {
        setRadioSourceValue(Number(event.target.value));
    };

    const onSearchChange = (text: string) => {
        if (text || []) {
            setSearchText(text.toLowerCase());
        }
    };

    const handleUploadDialogToggle = () => {
        setUploadDialogOpen(!uploadDialogOpen);
    };

    const handleEditDialogToggle = (document: IDocument | null) => {
        setDocumentToEdit(document);
        setSaving(false);
        setEditDialogOpen(!editDialogOpen);
    };

    const handleDownloadFile = async (document: IDocument) => {
        const response = await apiGet(
            `projects/all/${projectId}/documents/${document.id}/content`
        );

        const fileBlob = await response.blob();
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        let filename: string = "";
        let contentDisposition = response.headers.get("content-disposition");
        if (contentDisposition != null) {
            const matches = filenameRegex.exec(contentDisposition);
            filename = matches != null ? matches[1].replace(/['"]/g, "") : "";
        }
        downloadBlob(fileBlob, filename);
    };

    const downloadBlob = (blob, filename) => {
        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
            return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.href = url;
        a.setAttribute("type", "hidden");
        a.setAttribute("download", filename);
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleUploadError = (error: IDocumentError) => {
        let errorMessage: string;
        switch (error.errorCode) {
            case "ResourceAlreadyExists":
                errorMessage = `Failed to upload. File with name '${error.fileName}' already exists in project id '${projectId}'.`;
                break;
            case "ResourceNotFound":
                errorMessage = `Failed to upload. Project with id '${projectId}' does not exist.`;
                break;
            case "DocumentFileExtensionNotAllowed":
                errorMessage = `Failed to upload '${error.fileName}'. File type not allowed.`;
                break;
            default:
                errorMessage = `Error uploading ${error.fileName}, please try again`;
                break;
        }

        enqueueSnackbar(errorMessage, {
            variant: "error"
        });
        handleUploadDialogToggle();
    };

    const handleUploadComplete = (error: IDocumentError | null) => {
        if (error == null) {
            enqueueSnackbar(`Upload successfully complete.`, {
                variant: "success"
            });
        } else {
            handleUploadError(error);
        }
        refreshProjectDocuments();
        handleUploadDialogToggle();
    };

    const handleEditSave = async (document: IDocumentEdited) => {
        setSaving(true);

        try {
            await apiPatch(
                `projects/all/${projectId}/documents/${document.id}`,
                document
            );
        } catch (error) {
            let errorMessage: string;

            switch (error.errorCode) {
                case "NotEmptyValidator":
                    errorMessage = "Description must not be empty.";
                    break;
                case "MaximumLengthValidator":
                    errorMessage =
                        "Description must not be longer than 1024 characters.";
                    break;
                default:
                    errorMessage = "Error updating document, please try again";
                    break;
            }

            enqueueSnackbar(errorMessage, {
                variant: "error"
            });
        }

        handleEditDialogToggle(null);
        refreshProjectDocuments();
    };

    const classes = useStyles();

    return (
        <div className={classes.root}>
            {showFilters ? (
                <div className={classes.search}>
                    <SearchBox
                        onSearchChange={onSearchChange}
                        placeholder="Search Document Title, Description, Uploaded By"
                        label="Search Documents"
                        value={searchText}
                    />
                </div>
            ) : (
                <></>
            )}
            {showFilters ? (
                <FormControl className={classes.formControl}>
                    <RadioGroup
                        row
                        name="radioSources"
                        value={radioSourceValue}
                        onChange={handleSourceFilterChange}
                    >
                        <FormControlLabel
                            value={ProjectDocumentSource.All}
                            classes={{ label: classes.label }}
                            control={<Radio color="primary" />}
                            label="All"
                        />
                        <FormControlLabel
                            value={ProjectDocumentSource.Customer}
                            classes={{ label: classes.label }}
                            control={
                                <Radio
                                    className={classes.radio}
                                    color="primary"
                                />
                            }
                            label="Customer"
                        />
                        <FormControlLabel
                            value={ProjectDocumentSource.Tracerco}
                            classes={{ label: classes.label }}
                            control={
                                <Radio
                                    className={classes.radio}
                                    color="primary"
                                />
                            }
                            label="Tracerco"
                        />
                    </RadioGroup>
                </FormControl>
            ) : (
                <></>
            )}

            <Button
                onClick={handleUploadDialogToggle}
                className={classes.buttons}
                variant={"outlined"}
                color="primary"
            >
                Upload Documents
            </Button>

            <DocumentsTable
                isLoading={isLoadingDocuments || isLoadingCategories}
                isProcessing={false}
                documents={filteredSearchDocuments}
                onRemoveDocument={removeDocumentHandler}
                onDownloadRequest={handleDownloadFile}
                onEdit={handleEditDialogToggle}
                categories={categories}
            />
            <UploadDialog
                open={uploadDialogOpen}
                projectId={projectId}
                onCancel={handleUploadDialogToggle}
                onComplete={handleUploadComplete}
                categories={userCategories}
            />
            {documentToEdit !== null ? (
                <EditDocumentDialog
                    open={editDialogOpen}
                    document={documentToEdit}
                    handleSave={handleEditSave}
                    handleCancel={() => {
                        handleEditDialogToggle(null);
                    }}
                    saving={saving}
                    categories={userCategories}
                />
            ) : (
                <></>
            )}
        </div>
    );
};

export default ManageProjectDocumentsContainer;
